import { useState, useMemo, useCallback } from "react";

export default function useTable(
  data = [],
  initialRowsPerPage = 5,
  searchFields = []
) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [search, setSearch] = useState("");

  // Filter
  const filteredData = useMemo(() => {
    if (!search) return data;

    const lower = search.toLowerCase();

    return data.filter(item =>
      searchFields.some(field =>
        String(item[field] || "")
          .toLowerCase()
          .includes(lower)
      )
    );
  }, [data, search, searchFields]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  // Stable handlers — only reset page when value actually changes.
  // useCallback keeps the function reference stable across renders so that
  // child components (e.g. TableTopControls debounce effect) don't retrigger
  // and accidentally reset the page on every render.
  const handleSearch = useCallback((value) => {
    setSearch((prev) => {
      if (prev === value) return prev;
      setCurrentPage(1);
      return value;
    });
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setRowsPerPage((prev) => {
      if (prev === size) return prev;
      setCurrentPage(1);
      return size;
    });
  }, []);

  // Showing range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  return {
    currentPage,
    totalPages,
    paginatedData,
    search,
    rowsPerPage,
    totalItems,
    startItem,
    endItem,
    setSearch: handleSearch,
    setCurrentPage,
    setRowsPerPage: handlePageSizeChange
  };
}
